import { PersonAppearance } from '@app/modules/persons/appearances/types';
import { TimeRange } from '@mi-front/core';
import { Box } from '@mui/material';
import { BAR_HEIGHT_PX, BAR_TOP_PX } from './lane-mini-timeline-layout';
import { personAppearanceKey } from './person-appearance-key';
import {
  appearanceBarEmphasisSx,
  appearanceBarSx,
  MINI_TIMELINE_BAR_RADIUS_PX,
} from './persons-track-visual-tokens';

type LaneMiniTimelineBarsProps = {
  items: PersonAppearance[];
  range: TimeRange<number>;
  color: string;
  source: PersonAppearance['source'];
  hoveredKey: string | null;
  onHoverKey: (key: string | null) => void;
  onPlay: (appearance: PersonAppearance) => void;
};

/** Converts a timestamp to a percentage along the focus range. */
function pct(ts: number, range: TimeRange<number>): number {
  const span = range.to - range.from;
  if (span <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(0, ((ts - range.from) / span) * 100));
}

/** Renders appearance bars on the shared single-lane track. */
export function LaneMiniTimelineBars({
  items,
  range,
  color,
  source,
  hoveredKey,
  onHoverKey,
  onPlay,
}: LaneMiniTimelineBarsProps) {
  const anyHovered = hoveredKey !== null;
  return (
    <>
      {items
        .filter((a) => a.to > range.from && a.from < range.to)
        .map((a) => {
          const key = personAppearanceKey(a);
          const emphasized = hoveredKey === key;
          const left = pct(a.from, range);
          const width = Math.max(pct(a.to, range) - left, 1);
          return (
            <Box
              key={key}
              onMouseEnter={() => onHoverKey(key)}
              onClick={(e) => {
                e.stopPropagation();
                onPlay(a);
              }}
              sx={{
                left: `${left}%`,
                width: `${width}%`,
                height: BAR_HEIGHT_PX,
                top: BAR_TOP_PX,
                ...appearanceBarSx(color, source),
                ...appearanceBarEmphasisSx({
                  dimmed: anyHovered && !emphasized,
                  emphasized,
                  color,
                }),
                // Emphasis sets position:relative for track StreamEl; keep absolute here.
                position: 'absolute',
                borderRadius: `${MINI_TIMELINE_BAR_RADIUS_PX}px`,
              }}
            />
          );
        })}
    </>
  );
}
